import styles from "./CardRowSkeleton.module.css";

// Shared shimmer placeholder for horizontal card rows (event cards, category
// tiles, artist cards, ...). Several client components mount a Swiper only
// after hydration and need something to show for that first tick — this
// mirrors the real card's proportions (aspect ratio, radius, body lines) so
// swapping in the Swiper doesn't change the row's shape.
//
// Two sizing modes, matching how the real cards are actually sized:
// - `ratio` (default): the real card's image height is a function of its
//   width (schema.org-style `aspect-ratio`) — most cards work this way.
// - `fixedHeight`: the real card's image has one fixed pixel height
//   regardless of width (e.g. BlogCard.js's `.blogCardImage { height:
//   180px }`) — using `ratio` here would drift further from the real size
//   the narrower/wider the card renders.
//
// `variant="titleThenMedia"` covers cards shaped like ArtisCard.js: a fixed
// *total* card height (`cardHeightClassName`, e.g. "h-[248px] sm:h-[276px]")
// containing a title row on top and the image filling the rest — using the
// same flex layout as the real card (rather than guessing a split in
// pixels) so the header/image proportions track correctly at any
// screen size.
export default function CardRowSkeleton({
  count = 4,
  ratio = "2 / 1",
  radius = 16,
  withBody = true,
  fixedHeight,
  variant,
  cardHeightClassName = "",
  cardClassName = "",
  gap,
}) {
  if (variant === "titleThenMedia") {
    return (
      <div
        aria-hidden="true"
        className={styles.row}
        style={gap !== undefined ? { gap } : undefined}
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className={`${styles.titleMediaCard} ${cardHeightClassName} ${cardClassName}`}
            style={{ borderRadius: radius }}
          >
            <div className={styles.titleMediaHeader}>
              <div className={`${styles.line} ${styles.titleLine}`} />
            </div>
            <div className={styles.media} style={{ flex: 1 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      className={styles.row}
      style={gap !== undefined ? { gap } : undefined}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`${styles.card} ${cardClassName}`}>
          <div
            className={styles.media}
            style={
              fixedHeight
                ? { height: fixedHeight, borderRadius: radius }
                : { aspectRatio: ratio, borderRadius: radius }
            }
          />
          {withBody && (
            <div className={styles.body}>
              <div className={styles.line} />
              <div className={`${styles.line} ${styles.short}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
